// Utility helpers for appointments, prescriptions, and stale-evidence calculations.

export const STALE_EVIDENCE_MONTHS = 6;

export const APPOINTMENT_TYPES = [
  { id: "cp_exam",        label: "C&P Exam",            color: "#DC2626" },
  { id: "va_visit",       label: "VA Medical Visit",    color: "#1D4ED8" },
  { id: "civilian_visit", label: "Civilian Doctor",     color: "#0EA5E9" },
  { id: "therapy",        label: "Therapy / Counseling", color: "#7C3AED" },
  { id: "lab",            label: "Lab / Imaging",       color: "#0891B2" },
  { id: "vso_meeting",    label: "VSO Meeting",         color: "#F59E0B" },
  { id: "hearing",        label: "BVA Hearing",         color: "#BE123C" },
  { id: "other",          label: "Other",               color: "#64748B" },
];
export const appointmentTypeMeta = (id) => APPOINTMENT_TYPES.find((t) => t.id === id) || APPOINTMENT_TYPES[APPOINTMENT_TYPES.length - 1];

export function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function parseDateOnly(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}

export function daysBetween(a, b) {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function formatRelativeDay(targetDate) {
  if (!targetDate) return "";
  const diff = daysBetween(startOfToday(), targetDate);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff <= 14) return `In ${diff} days`;
  if (diff < -1 && diff >= -14) return `${Math.abs(diff)} days ago`;
  return targetDate.toLocaleDateString();
}

// === Prescriptions ===
export function rxNextRefillDate(rx) {
  const last = parseDateOnly(rx.lastFilled);
  const supply = Number(rx.daysSupply || 0);
  if (!last || supply <= 0) return null;
  const next = new Date(last);
  next.setDate(next.getDate() + supply);
  return next;
}

export function rxDaysUntilRefill(rx) {
  const next = rxNextRefillDate(rx);
  if (!next) return null;
  return daysBetween(startOfToday(), next);
}

export function rxStatus(rx) {
  // Returns: 'overdue' | 'due_soon' (<=7 days) | 'ok' | 'unknown'
  const days = rxDaysUntilRefill(rx);
  if (rx.archived) return "archived";
  if (days === null) return "unknown";
  if (days < 0) return "overdue";
  if (days <= 7) return "due_soon";
  return "ok";
}

// === Appointments ===
export function apptDate(appt) {
  if (!appt?.datetime) return null;
  const d = new Date(appt.datetime);
  if (isNaN(d.getTime())) return null;
  return d;
}

export function apptStatus(appt) {
  if (appt.completed) return "completed";
  const d = apptDate(appt);
  if (!d) return "unknown";
  const today = new Date();
  const days = daysBetween(today, d);
  if (days < 0) return "overdue";
  if (days <= (appt.remindDaysBefore ?? 1)) return "due_soon";
  return "ok";
}

// === Stale evidence (6 months default) ===
export function evidenceAgeDays(ev) {
  const d = parseDateOnly(ev?.date);
  if (!d) return null;
  return daysBetween(d, startOfToday());
}

export function isStaleEvidence(ev, months = STALE_EVIDENCE_MONTHS) {
  const days = evidenceAgeDays(ev);
  if (days === null) return false;
  return days >= months * 30;
}
